<template>
  <section class="filters" data-cy="certificates-filter-menu">
    <div class="filters__actions">
      <input
        type="button"
        value="Clear"
        class="filter-button-clear"
        @click.prevent="clearFilters"
        data-cy="clear-filters-button"
      />
      <input
        type="button"
        value="Apply"
        class="filter-button-apply"
        @click.prevent="applyFilters"
        data-cy="apply-filters-button"
      />
    </div>
    <ul class="filters__list">
      <li data-cy="filters-item">
        <label data-cy="filters-item-label">PLANT</label>
        <MultiSelect
          class="certificates-plants-select"
          :items="plants"
          v-model:value="selectedPlants"
          :auto-close="false"
          placeholder="Select Plants"
          itemsName="Plants"
          data-cy="plant-select"
        />
      </li>
      <li data-cy="filters-item">
        <label data-cy="filters-item-label">ACTIVITY DATE RANGE</label>
        <DropDownList
          class="filters-dropdown"
          :data-items="dateRangeValues"
          :text-field="'text'"
          v-model="selectedDateRange"
          placeholder="All"
          data-cy="date-range-select"
        />
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
import { DropDownList } from "@progress/kendo-vue-dropdowns";
import { ref, watchEffect } from "vue";
import MultiSelect from "@/shared/ui/inputs/MultiSelect.vue";
import type { IdAndName } from "@/Domain/IdAndName";

export interface CertificatesMobileFilters {
  daysBack: number;
  plants: IdAndName[];
}

const props = defineProps<{
  plants: IdAndName[];
  selectedPlants: IdAndName[];
  selectedDaysBack: number;
}>();

const emits = defineEmits<{
  (event: "apply", filters: CertificatesMobileFilters): void;
}>();

const dateRangeValues = [
  { text: "All", daysBack: 0 },
  { text: "Yesterday", daysBack: 1 },
  { text: "Last 7 Days", daysBack: 7 },
  { text: "Last 30 Days", daysBack: 30 },
];

const selectedPlants = ref<IdAndName[]>([...props.plants]);
const selectedDateRange = ref(dateRangeValues[0]);

const applyFilters = () => {
  emits("apply", {
    daysBack: selectedDateRange.value.daysBack,
    plants: selectedPlants.value,
  } as CertificatesMobileFilters);
};

const clearFilters = () => {
  emits("apply", {
    daysBack: 0,
    plants: props.plants,
  } as CertificatesMobileFilters);
};

watchEffect(() => {
  selectedPlants.value = props.selectedPlants;
  selectedDateRange.value = dateRangeValues.find(
    (x) => x.daysBack === props.selectedDaysBack
  )!;
});
</script>

<style lang="scss">
.filters {
  position: absolute;
  background: #fff;
  box-shadow: 0 3px 15px rgba(0, 0, 0, 0.3);
  width: 100%;
  padding: 20px;
  overflow: auto;
  display: block;
  z-index: 100;
  height: 500px;

  &__actions {
    padding: 15px;
    border-bottom: 1px solid #ddd;
    margin: -20px -20px 10px;
    background: #efefef;
    justify-content: flex-end;
    display: flex;

    .filter-button-clear {
      text-align: center;
      padding: 8px 20px;
      box-shadow: none;
      border: 1px solid #3a5da6;
      font-size: 11px;
      text-transform: uppercase;
      font-weight: 600;
      cursor: pointer;
      border-radius: 2px;
      background: 0 0;
      color: #3a5da6 !important;
      display: block;
      margin: 0 5px;
    }

    .filter-button-apply {
      text-align: center;
      color: #fff !important;
      padding: 8px 20px;
      box-shadow: none;
      background: #3a5da6;
      border: 1px solid #3a5da6;
      font-size: 11px;
      text-transform: uppercase;
      font-weight: 600;
      cursor: pointer;
      border-radius: 2px;
      display: block;
      margin: 0 5px;
    }
  }

  &__list {
    padding-left: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;

    li {
      list-style-type: none;
      display: flex;
      flex-direction: column;
    }

    label {
      font-weight: 500;
      font-size: 11px;
      text-transform: uppercase;
      color: #999;
      margin-bottom: 5px;
      display: inline-block;
    }

    .filters-dropdown {
      width: 100%;
      margin-bottom: 8px;
    }
  }
}
</style>
